/**
 * 이상한 방 - 선택 외곽선 (에디터와 체인저 도구가 함께 쓴다)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 3.1절(가려진 위치 허용) · 4.2절(선택 → 동사)
 *            로드맵 M4(`changerTool` - 선택 외곽선은 `editor`의 것을 복붙하지 않고 `render/`에 공용으로 뽑는다)
 *
 * `input/editor.ts`에 살던 것을 그대로 옮겼다. 사람 체인저의 도구(`input/changerTool.ts`)가 같은 외곽선을
 * 필요로 하게 됐고, 두 파일이 각자 `LineSegments`를 만들면 색·두께·`depthTest`가 언젠가 갈린다 - "고른 물건"이
 * 꾸미기와 바꾸기에서 다른 물건으로 보이면 그것이 곧 두 번째 UI 문법이다. 그래서 여기가 원본이고 둘은 부른다.
 *
 * ## `depthTest: false`인 이유
 *
 * 3.1절이 가려진 위치를 허용하므로 소파 뒤의 물건도 선택돼야 하는데, 외곽선이 소파에 가리면 그 다음 조작
 * (동사 고르기)을 할 근거가 화면에 없다. 고스트 외곽선(`gridHelper.ts`)과 같은 결정이다.
 *
 * ## 유닛 박스 하나를 스케일로 맞춘다
 *
 * 물건마다 지오메트리를 굽지 않는다. 모서리만 그린 1m 박스 하나를 두고 매번 `scale`로 크기를 맞춘다 -
 * 선택이 바뀔 때마다 GPU 자원이 생기고 사라지는 경로를 만들지 않기 위해서다.
 *
 * 렌더 레이어이므로 Three.js 객체의 제자리 변경을 허용한다(코딩 규칙의 명시적 예외).
 */

import { BoxGeometry, EdgesGeometry, LineBasicMaterial, LineSegments } from 'three'

import type { CellRect } from '../core/floorPlan'
import { CELL_SIZE_METERS, type CatalogEntry, type ObjectInstance } from '../core/types'
import { rectToWorldBox } from './lighting'
import { computeObjectCenterWorld, disposeObject3D, footprintToMeters, rotToRadians } from './objectView'

/** 선택 외곽선 색. 고스트(녹/적)와 겹치지 않는 색이라야 "선택"과 "배치 가능"이 구별된다. */
const SELECTION_COLOR = 0xffcc44
const SELECTION_OPACITY = 0.95

/** 외곽선을 몸통보다 살짝 키운다. 같은 크기면 표면과 겹쳐 지글거린다. */
const SELECTION_OUTLINE_SCALE = 1.05

const UNIT_SIZE = 1

/**
 * 구획 테두리의 색·불투명도(M11). 노랑의 **그늘색**(`--gh-accent-shade`)이다 - 같은 노랑(`SELECTION_COLOR`)으로
 * 그렸더니 크림색 바닥 위에서 불투명도를 0.9까지 올려도 선이 보이지 않았다(실측). 대비가 문제이지 세기가 아니었다.
 * 그늘색은 노랑의 가족 안에 있으면서 파스텔 바닥과 갈리므로 "내 것"이라는 말을 지키면서 읽힌다. 고른 물건(밝은 노랑,
 * 0.95)과도 저절로 구별된다 - 하나는 밝고 하나는 어둡다.
 */
const ZONE_COLOR = 0x7a621c
const ZONE_OPACITY = 0.85

/**
 * 구획 상자의 높이(m). 바닥에서 무릎쯤 - 천장까지 세우면 컷어웨이가 잘라 낸 벽 위로 선이 뜨고, 0이면 바닥과 겹쳐
 * 지글거린다(`gridHelper`의 y 오프셋과 같은 문제). 소품 대부분보다 낮아 방을 가리지 않는다.
 */
const ZONE_HEIGHT_METERS = 0.5

/** 유닛 박스의 모서리 선. 색·불투명도·이름만 다른 두 외곽선(고른 물건 · 내 구획)이 이 한 함수에서 나온다. */
function createOutline(name: string, color: number, opacity: number): LineSegments {
  const source = new BoxGeometry(UNIT_SIZE, UNIT_SIZE, UNIT_SIZE)
  const geometry = new EdgesGeometry(source)
  // EdgesGeometry가 정점을 복사해 가므로 원본은 즉시 해제한다.
  source.dispose()

  const outline = new LineSegments(
    geometry,
    new LineBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthTest: false,
    }),
  )
  outline.name = name
  outline.visible = false
  // depthTest를 끈 선은 마지막에 그려야 의도대로 항상 위에 보인다.
  outline.renderOrder = 1

  return outline
}

/**
 * 선택 외곽선. 유닛 박스의 모서리만 그려 두고 매번 스케일로 크기를 맞춘다.
 * 만든 직후에는 보이지 않는다 - 고른 것이 없는 상태가 기본이다.
 */
export function createSelectionOutline(): LineSegments {
  return createOutline('selection-outline', SELECTION_COLOR, SELECTION_OPACITY)
}

/**
 * 내 구획의 테두리(로드맵 M11 - 바꾸기 60초 동안 "여기까지가 내 것"을 선 하나로).
 *
 * **새 렌더 경로를 만들지 않는다.** 선택 외곽선과 같은 유닛 박스·같은 머티리얼이고 다른 것은 불투명도와 크기 계산뿐이다 -
 * 구획 강조를 위해 바닥 메시를 물들이거나 조명을 건드리면 그 순간 "어두워서 못 찾는다"가 생기고(`lighting.ts` 제1 제약)
 * 컷어웨이·전환이 그 상태를 알아야 할 물건이 하나 더 는다. 선은 아무것도 가리지 않는다.
 */
export function createZoneOutline(): LineSegments {
  return createOutline('zone-outline', ZONE_COLOR, ZONE_OPACITY)
}

/**
 * 외곽선을 물건 하나에 맞춘다. 물건이나 카탈로그 항목이 없으면 감춘다 -
 * "고른 것이 사라졌다"(남이 치웠다 · 되돌렸다)를 부르는 쪽이 따로 판단하지 않아도 화면이 맞는다.
 */
export function placeSelectionOutline(
  outline: LineSegments,
  obj: ObjectInstance | undefined,
  entry: CatalogEntry | undefined,
): void {
  if (obj === undefined || entry === undefined) {
    outline.visible = false
    return
  }

  const center = computeObjectCenterWorld(obj.cell, entry, obj.rot)
  const size = footprintToMeters(entry.footprint)

  outline.position.set(center.x, center.y, center.z)
  outline.rotation.y = rotToRadians(obj.rot)
  outline.scale.set(
    size.x * SELECTION_OUTLINE_SCALE,
    size.y * SELECTION_OUTLINE_SCALE,
    size.z * SELECTION_OUTLINE_SCALE,
  )
  outline.visible = true
}

/**
 * 구획 테두리를 셀 사각형 하나에 맞춘다. `null`이면 감춘다(구획이 없는 판 · 바꾸기 밖).
 * 셀 → 월드는 `lighting.rectToWorldBox`다 - 조명 리그가 구획 상자를 얻는 것과 **같은 함수**라
 * 여기서 축척 곱셈을 다시 적지 않는다(좌표 계약이 두 벌이 되면 선이 반 칸 어긋난다).
 */
export function placeZoneOutline(outline: LineSegments, rect: CellRect | null): void {
  if (rect === null) {
    outline.visible = false
    return
  }

  const box = rectToWorldBox(rect, 0, ZONE_HEIGHT_METERS / CELL_SIZE_METERS)
  outline.position.set(box.center.x, box.center.y, box.center.z)
  outline.rotation.y = 0
  outline.scale.set(box.size.x, box.size.y, box.size.z)
  outline.visible = true
}

/** 씬에서 떼고 GPU 자원을 놓는다. Three.js는 GC 하지 않는다 - 씬에서 빼는 것만으로는 지오메트리가 남는다. */
export function disposeSelectionOutline(outline: LineSegments): void {
  outline.removeFromParent()
  disposeObject3D(outline)
}
